import {ReactNode, useContext, useEffect, useMemo, useRef, useState} from 'react';
import './select.scss';
import Popup from '../popup';
import classNames from '../../lib/class-names';
import {MenuContext} from '../popup/context';
import {observer} from 'mobx-react';
import generateKeyHandler from '../popup/key-events';
import {HeaderContext} from '../header/context';
import {ComboBox, Dropdown} from '@carbon/react';
import {useCombobox} from 'downshift';
import Fuse from 'fuse.js';

export type BasicSelectType = {
    value: string
    label: string
}

type Props<T> = {
    data: Array<T>
    value: string
    onChange: (opt: string) => void
    children: [(item: T, i: number) => string, (item: T, i: number) => ReactNode]
    searchable?: boolean
    disabled?: boolean
    optionDisabled?: (item: T, i: number) => boolean
    width?: number,
    defaultEmpty?: boolean,
    rightAlign?: boolean,
    titleText?: ReactNode | string,
    required?: boolean,
    itemToString?: (item: T, i: number) => string,
    enableFuseSearch?: boolean,
    fuseThreshold?: number,
    fuseKeys?: string[],
    ['data-testid']?: string
    autoAlign?: boolean
}

const Select = observer(function Select<T>(props: Props<T>) {
    let {data, value, onChange, searchable, disabled, optionDisabled, width, rightAlign, titleText, required, itemToString, enableFuseSearch = true, fuseThreshold = 0.3, fuseKeys, autoAlign = false} = props;
    optionDisabled = optionDisabled || (() => false);
    const [getValue, getLabel] = props.children;
    const menuStore = useContext(MenuContext);
    const [focusIndex, setFocus] = useState(-1);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [timeout, setSearchTimeout] = useState<any>(null); // Type is actually something like NodeJS.Timeout. But timeouts are actually numbers. Anyway, it's set to `any` to make the compiler happy
    const [search, setSearch] = useState<string>('');
    const {useNewDesign} = useContext(HeaderContext);
    const downshiftActions = useRef();

    // Create searchable data with labels for Fuse.js
    const searchableData = useMemo(() => {
        return data.map((item, i) => {
            const display = props.children[1](item, i);
            const label: string = typeof display === 'string' ? display : props.children[0](item, i);
            return {
                item,
                index: i,
                label,
            };
        });
    }, [data, props.children]);

    // Create Fuse instance for fuzzy search
    const fuse = useMemo(() => {
        if (!enableFuseSearch || !searchable || searchableData.length === 0) return null;
        
        // If custom keys are provided, use them for searching the original data
        // Otherwise, search by the label we extracted
        const keys = fuseKeys && fuseKeys.length > 0 ? fuseKeys : ['label'];
        
        return new Fuse(searchableData, {
            threshold: fuseThreshold,
            keys: keys,
            ignoreLocation: true,
            findAllMatches: true,
        });
    }, [searchableData, enableFuseSearch, searchable, fuseThreshold, fuseKeys]);

    const handleClick = (item: T, i: number) => {
        if (optionDisabled!(item, i)) return;
        onChange(getValue(item, i));
    };

    const [label, activeIndex] = useMemo(() => {
        let label: ReactNode = <span>&nbsp;</span>;
        let index = -1;
        for (let i = 0; i < data.length; i++) {
            if (getValue(data[i], i) === value) {
                label = getLabel(data[i], i);
                index = i;
                break;
            }
        }
        return [label, index];
    }, [value, data, getLabel, getValue]);

    // Add keyboard listener for keyboard interaction
    useEffect(() => {
        if (!menuStore.visible) setFocus(-1);
    }, [menuStore.visible]);

    // Focus on items based on the search string
    useEffect(() => {
        if (!search || !data.length || searchable) return;
        const index = data.findIndex((item, i) => {
            const display = props.children[1](item, i);
            const value: string = typeof display === 'string' ? display : props.children[0](item, i);
            return value
                .toLowerCase()
                .replace(/[^a-z0-9]/g, '')
                .startsWith(search);
        });
        if (index >= 0) setFocus(index);
    }, [data, onChange, props.children, search, searchable]);

    const items = useMemo(() => {
        const zipped: Array<[T, number]> = data.map((item, i) => [item, i]);
        if (!searchable || !search) return zipped;
        
        // Use Fuse.js search if enabled
        if (enableFuseSearch && fuse) {
            const fuseResults = fuse.search(search);
            const resultIndices = new Set(fuseResults.map(result => result.item.index));
            // eslint-disable-next-line
            return zipped.filter(([_, i]) => resultIndices.has(i));
        }
        
        // Fallback to basic string matching
        const query = search.toLowerCase();
        return zipped.filter(([item, i]) => {
            const display = props.children[1](item, i);
            const value: string = typeof display === 'string' ? display : props.children[0](item, i);
            return value.toLowerCase().includes(query);
        });
    }, [data, searchable, search, props.children, enableFuseSearch, fuse]);

    const handleKey = generateKeyHandler(
        menuStore,
        focusIndex,
        data.length,
        setFocus,
        (i) => handleClick(data[i], i),
        setSearch,
        timeout,
        setSearchTimeout,
    );

    if (useNewDesign) {
        const DropdownElement = searchable ? ComboBox : Dropdown;
        const selectedValue = useMemo(() => {
            for (let i = 0; i < data.length; i++) {
                if (getValue(data[i], i) === value) {
                    return [data[i], i] as [T, number];
                }
            }
        }, [value, data, getValue]);
        
        return <DropdownElement 
            className={rightAlign ? 'right' : ''}
            disabled={disabled} 
            hideLabel
            autoAlign={autoAlign}
            downshiftActions={downshiftActions}
            downshiftProps={{stateReducer: (state, actionAndChanges) => {
                const {changes, type} = actionAndChanges;
                
                switch (type) {
                case useCombobox.stateChangeTypes.InputClick:
                case useCombobox.stateChangeTypes.ToggleButtonClick:
                    if(changes.isOpen) {
                        return {
                            ...changes,
                            inputValue: '',
                        };
                    }
                    return changes;
                    
                case useCombobox.stateChangeTypes.InputBlur:
                    return {
                        ...changes,
                        inputValue: selectedValue ? String(getLabel(selectedValue[0], selectedValue[1])) : '',
                        selectedItem: selectedValue,
                    };
                case useCombobox.stateChangeTypes.FunctionSelectItem:
                    //When user clear dropdown
                    if(!required && !changes.isOpen && changes.selectedItem === null && changes.inputValue === '' && state.inputValue !== '') {
                        return {
                            ...changes,
                            inputValue: '',
                            selectedItem: null,
                        };
                    }
                    else {
                        return {
                            ...changes,
                            inputValue: selectedValue ? String(getLabel(selectedValue[0], selectedValue[1])) : '',
                            selectedItem: selectedValue,
                        };
                    }
                default:
                    return changes;
                }
            }}}
            placeholder={selectedValue ? String(getLabel(selectedValue[0], selectedValue[1])) : ''}
            items={items} 
            initialSelectedItem={selectedValue}
            selectedItem={selectedValue}
            label={titleText || ''} 
            id='select'
            titleText={titleText || ''}
            itemToElement={(p) => p ? <>{getLabel(p[0], p[1])}</> : <></>}
            itemToString={(p) => p ? (itemToString ? itemToString(p[0], p[1]) : String(getLabel(p[0], p[1]))) : ''}
            onChange={(e) => {
                if(required && !e.selectedItem) return;
                e.selectedItem ? onChange(getValue(e.selectedItem[0], e.selectedItem[1])) : onChange('');
            }}
            onInputChange={(text) => setSearch(text)}
            {...(props['data-testid'] ? {'data-testid': props['data-testid']} : {})}
        />;
    }

    return (
        <div className='select-holder'>
            <button
                className={`select ${menuStore.visible && 'open'}`}
                style={{width: (width || '300') + 'px'}}
                ref={buttonRef}
                onKeyDown={handleKey}
                role='combobox'
                aria-controls='popup-menu'
                aria-expanded={menuStore.visible ? 'true' : 'false'}
                disabled={disabled}
                {...(props['data-testid'] ? {'data-testid': props['data-testid']} : {})}
            >
                {label}
            </button>
            <Popup targetRef={buttonRef} matchWidth scrollIndex={focusIndex}>
                {searchable ? (
                    <div className='select-search-holder'>
                        <div className='mx-2 mb-2'>
                            <input
                                type='text'
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                placeholder='Search Options'
                            />
                        </div>
                        <div className='select-search-items'>
                            {items.map(([item, i]) => (
                                <button
                                    key={i}
                                    onClick={() => handleClick(item, i)}
                                    className={classNames('menu-item', {
                                        'is-active': i === activeIndex,
                                        'is-focus': i === focusIndex,
                                        'is-disabled': optionDisabled!(item, i),
                                    })}
                                >
                                    {getLabel(item, i)}
                                </button>
                            ))}
                        </div>
                    </div>
                ) : (
                    items.map(([item, i]) => (
                        <button
                            key={i}
                            onClick={() => handleClick(item, i)}
                            className={classNames('menu-item', {
                                'is-active': i === activeIndex,
                                'is-focus': i === focusIndex,
                                'is-disabled': optionDisabled!(item, i),
                            })}
                        >
                            {getLabel(item, i)}
                        </button>
                    ))
                )}
            </Popup>
        </div>
    );
});

export default Select;
